import React, { useContext, useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Autocomplete,
  Avatar,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  FormControlLabel,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { DeleteOutline } from '@mui/icons-material'
import API from '../../../../services/API.js'
import { GlobalContext } from '../../../../App.js'

const fullName = (person) =>
  `${person?.firstName ?? ''} ${person?.lastName ?? ''}`.trim() || 'Unknown'

// Site Config → Fleet → Defect Alerts. Designated people who are told the
// moment a defect is reported (optionally only for vehicles at certain
// bases), whether the whole Fleet / Make Ready teams are told as well, and
// whether a vehicle reported as unfit is taken Out of Service automatically.
// How each person receives it (email / app) is their own choice in their
// notification settings under Fleet & Equipment.
const DefectAlertSettings = () => {
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [changesPresent, setChangesPresent] = useState(false)
  const [contacts, setContacts] = useState([])
  const [notifyTeams, setNotifyTeams] = useState(true)
  const [autoOutOfService, setAutoOutOfService] = useState(true)
  const [people, setPeople] = useState([])
  const [bases, setBases] = useState([])
  const [error, setError] = useState(null)

  const { setToast } = useContext(GlobalContext)

  useEffect(() => {
    API.get('/fleet/defect-alert-settings').then((res) => {
      if (res.status === 200) {
        setContacts(res.data.defectContacts ?? [])
        setNotifyTeams(res.data.notifyTeams !== false)
        setAutoOutOfService(res.data.autoOutOfService !== false)
        setPeople(res.data.options?.people ?? [])
        setBases(res.data.options?.baseLocations ?? [])
      } else {
        setError(res.data?.error ?? 'Could not load defect alert settings')
      }
      setLoading(false)
    })
  }, [])

  const peopleById = useMemo(
    () => new Map(people.map((person) => [person._id, person])),
    [people],
  )
  const basesById = useMemo(
    () => new Map(bases.map((base) => [base._id, base])),
    [bases],
  )
  const contactIds = new Set(contacts.map((contact) => contact.user))
  const addablePeople = people.filter((person) => !contactIds.has(person._id))

  const change = (updater) => {
    updater()
    setChangesPresent(true)
    setError(null)
  }

  const handleSaveChanges = () => {
    setSaving(true)
    API.put('/fleet/defect-alert-settings', {
      defectContacts: contacts,
      notifyTeams,
      autoOutOfService,
    }).then((res) => {
      setSaving(false)
      if (res.status === 200) {
        setContacts(res.data.defectContacts ?? [])
        setNotifyTeams(res.data.notifyTeams !== false)
        setAutoOutOfService(res.data.autoOutOfService !== false)
        setChangesPresent(false)
        setToast({ message: 'Changes Saved' })
      } else {
        setError(res.data?.error ?? 'Could not save defect alert settings')
      }
    })
  }

  return (
    <Stack spacing={2}>
      <Stack>
        <Typography variant="title2" fontWeight={700}>
          Defect Alerts
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Choose who is told as soon as a vehicle or equipment defect is
          reported — from the defect form or a failed vehicle daily inspection
          — so new defects are picked up and dealt with promptly.
        </Typography>
        {loading && <LinearProgress sx={{ mt: 2 }} />}
      </Stack>

      {error && <Alert severity="error">{error}</Alert>}

      <Card elevation={0} variant="outlined" sx={{ borderRadius: 2 }}>
        <CardHeader
          title="Designated defect contacts"
          subheader="These people are alerted about every new defect. Limit someone to particular bases and they are only alerted about vehicles at those bases — equipment defects always go to everyone listed."
          titleTypographyProps={{ variant: 'subtitle1', fontWeight: 600 }}
        />
        <Divider />
        <CardContent>
          <Stack spacing={2}>
            {!loading && !contacts.length && (
              <Typography variant="body2" color="text.secondary">
                No designated contacts yet.
              </Typography>
            )}

            {contacts.map((contact) => {
              const person = peopleById.get(contact.user)
              return (
                <Stack
                  key={contact.user}
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1.5}
                  alignItems={{ xs: 'stretch', sm: 'center' }}
                  sx={{
                    p: 1.5,
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 2,
                  }}
                >
                  <Stack
                    direction="row"
                    spacing={1.5}
                    alignItems="center"
                    sx={{ minWidth: { sm: 220 }, flexShrink: 0 }}
                  >
                    <Avatar src={person?.photograph} />
                    <Stack minWidth={0}>
                      <Typography variant="body2" fontWeight={600} noWrap>
                        {person ? fullName(person) : 'No longer eligible'}
                      </Typography>
                      <Typography
                        variant="caption"
                        color={person ? 'text.secondary' : 'error'}
                        noWrap
                      >
                        {person
                          ? person.email
                          : 'Not on Fleet, Make Ready or Governance — remove them to save'}
                      </Typography>
                    </Stack>
                  </Stack>
                  <Autocomplete
                    multiple
                    size="small"
                    fullWidth
                    options={bases.map((base) => base._id)}
                    getOptionLabel={(id) => basesById.get(id)?.name ?? 'Unknown base'}
                    value={contact.baseLocations}
                    onChange={(e, value) =>
                      change(() =>
                        setContacts((prev) =>
                          prev.map((item) =>
                            item.user === contact.user
                              ? { ...item, baseLocations: value }
                              : item,
                          ),
                        ),
                      )
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Bases"
                        placeholder={
                          contact.baseLocations.length ? '' : 'All bases'
                        }
                        InputLabelProps={{ shrink: true }}
                      />
                    )}
                  />
                  <Tooltip title="Remove contact">
                    <IconButton
                      aria-label={`Remove ${fullName(person)}`}
                      onClick={() =>
                        change(() =>
                          setContacts((prev) =>
                            prev.filter((item) => item.user !== contact.user),
                          ),
                        )
                      }
                      sx={{ alignSelf: { xs: 'flex-end', sm: 'center' } }}
                    >
                      <DeleteOutline />
                    </IconButton>
                  </Tooltip>
                </Stack>
              )
            })}

            <Autocomplete
              options={addablePeople}
              value={null}
              blurOnSelect
              getOptionLabel={fullName}
              isOptionEqualToValue={(a, b) => a._id === b._id}
              onChange={(e, person) => {
                if (!person) return
                change(() =>
                  setContacts((prev) => [
                    ...prev,
                    { user: person._id, baseLocations: [] },
                  ]),
                )
              }}
              renderOption={(props, person) => {
                const { key, ...optionProps } = props
                return (
                  <li key={key} {...optionProps}>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Avatar
                        src={person.photograph}
                        sx={{ width: 28, height: 28 }}
                      />
                      <Stack>
                        <Typography variant="body2">
                          {fullName(person)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {person.email}
                        </Typography>
                      </Stack>
                    </Stack>
                  </li>
                )
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Add a designated contact"
                  helperText="Anyone on the Fleet, Make Ready or Governance team, or a site admin — the people who can open a defect."
                />
              )}
            />
          </Stack>
        </CardContent>
      </Card>

      <Card elevation={0} variant="outlined" sx={{ borderRadius: 2 }}>
        <CardHeader
          title="Fleet and Make Ready teams"
          subheader="Switch this off once you have designated contacts if you only want them to be alerted."
          titleTypographyProps={{ variant: 'subtitle1', fontWeight: 600 }}
        />
        <Divider />
        <CardContent>
          <FormControlLabel
            control={
              <Switch
                checked={notifyTeams}
                onChange={(e) => {
                  const checked = e.target.checked
                  change(() => setNotifyTeams(checked))
                }}
              />
            }
            label={
              notifyTeams
                ? 'Everyone on the Fleet and Make Ready teams is alerted too'
                : 'Only designated contacts are alerted'
            }
          />
          {!notifyTeams && !contacts.length && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              With no designated contacts, nobody would be told about new
              defects. Add at least one contact before saving.
            </Alert>
          )}
        </CardContent>
      </Card>

      <Card elevation={0} variant="outlined" sx={{ borderRadius: 2 }}>
        <CardHeader
          title="Take unfit vehicles out of service"
          subheader="When someone reports a vehicle defect and says the vehicle is not fit for use, AmbuNet can mark that vehicle Out of Service straight away."
          titleTypographyProps={{ variant: 'subtitle1', fontWeight: 600 }}
        />
        <Divider />
        <CardContent>
          <FormControlLabel
            control={
              <Switch
                checked={autoOutOfService}
                onChange={(e) => {
                  const checked = e.target.checked
                  change(() => setAutoOutOfService(checked))
                }}
              />
            }
            label={
              autoOutOfService
                ? 'Vehicles reported as not fit for use are marked Out of Service automatically'
                : 'Vehicles stay in service until the fleet team changes their status'
            }
          />
        </CardContent>
      </Card>

      <Card elevation={0} variant="outlined" sx={{ borderRadius: 2 }}>
        <CardHeader
          title="How alerts are delivered"
          titleTypographyProps={{ variant: 'subtitle1', fontWeight: 600 }}
        />
        <Divider />
        <CardContent>
          <Stack spacing={1}>
            <Typography variant="body2" color="text.secondary">
              Every alert appears in the recipient's notification bell in
              AmbuNet. Each person also chooses whether it is emailed to them
              and/or pushed to the mobile app in their own notification
              settings, under <strong>Fleet &amp; Equipment → Defects
              reported</strong>. Email is on unless they change it.
            </Typography>
            <Stack direction="row" gap={1} flexWrap="wrap">
              <Chip size="small" label="In-app bell" />
              <Chip size="small" label="Email" />
              <Chip size="small" label="Mobile push" />
            </Stack>
            <Typography variant="caption" color="text.secondary">
              The person who reports a defect is never alerted about their own
              report.
            </Typography>
          </Stack>
        </CardContent>
      </Card>

      <Paper
        elevation={3}
        sx={{
          position: 'sticky',
          bottom: 0,
          p: 2,
          borderRadius: 3,
          background: '#ffffffcc',
          backdropFilter: 'blur(10px)',
          border: '1px solid #eee',
        }}
      >
        <Button
          size="large"
          variant="contained"
          fullWidth
          color="success"
          onClick={handleSaveChanges}
          disabled={!changesPresent || saving}
        >
          Save Changes
        </Button>
      </Paper>
    </Stack>
  )
}

export default DefectAlertSettings
